import type { ListBuilderConfiguration } from '../types';

/**
 * Ecommerce User Clients list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const ECOMMERCE_USER_CLIENTS_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-ecommerce-user-clients',
			cacheTTL: 600000,
			name: 'Ecommerce User Clients',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'client-name',
					headers: [
						{
							id: 'client-name-header',
							text: 'Client Name',
						},
					],
					fields: [
						{
							id: 'client-name-field',
							link: {
								type: 'object',
								enabled: true,
								idField: 'id',
								objectTable: 'client',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientName',
									objectTable: 'ecommerce_user_client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
							},
							displayStyle: 'text',
						},
						// PD-16789 — the link an inherited account comes through
						// (also shown on an own link that is disabled while
						// another link still grants the account).
						{
							id: 'client-name-line-break',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
							conditionalVisibility: {
								field: 'inheritedVia',
								condition: 'is_not_null',
							},
						},
						{
							id: 'inherited-via-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'inheritedVia',
									objectTable: 'ecommerce_user_client',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							prefix: 'via ',
							conditionalVisibility: {
								field: 'inheritedVia',
								condition: 'is_not_null',
							},
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'ecommerce_user_client',
							fieldName: 'clientName',
							direction: 'ASC',
						},
					],
					minWidth: 200,
					verticalAlignment: 'top',
				},
				// PD-16789 — whether the row is the user's own link or an
				// account inherited through one.
				{
					id: 'access-source',
					headers: [
						{
							id: 'access-source-header',
							text: 'Access',
						},
					],
					fields: [
						{
							id: 'access-source-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'accessSource',
									objectTable: 'ecommerce_user_client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								valueMapping: [
									{
										value: 'direct',
										displayText: 'Direct link',
									},
									{
										value: 'child',
										displayText: 'Child account',
									},
									{
										value: 'ownership',
										displayText: 'Ownership group',
									},
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 150,
					verticalAlignment: 'top',
				},
				// PD-16789 — a link's cascade settings, shown on the user's own
				// links only (an inherited row has no settings of its own). Edited
				// through the row's Edit action.
				{
					id: 'include-child-clients',
					headers: [
						{
							id: 'include-child-clients-header',
							text: 'Child Accounts',
						},
					],
					fields: [
						{
							id: 'include-child-clients-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'includeChildClients',
									objectTable: 'ecommerce_user_client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'conditional',
									conditionalRules: [
										{
											condition: 'equals',
											value: true,
											color: {
												type: 'theme',
												themeColor: 'success',
											},
										},
										{
											condition: 'equals',
											value: false,
											color: {
												type: 'theme',
												themeColor: 'textSecondary',
											},
										},
									],
								},
								valueMapping: [
									{
										value: true,
										displayText: 'Yes',
									},
									{
										value: false,
										displayText: 'No',
									},
								],
							},
							displayStyle: 'text',
							conditionalVisibility: {
								field: 'isInherited',
								condition: 'equals',
								value: false,
							},
						},
					],
					sortable: false,
					width: 140,
					verticalAlignment: 'top',
				},
				{
					id: 'include-ownership-clients',
					headers: [
						{
							id: 'include-ownership-clients-header',
							text: 'Ownership Accounts',
						},
					],
					fields: [
						{
							id: 'include-ownership-clients-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'includeOwnershipClients',
									objectTable: 'ecommerce_user_client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'conditional',
									conditionalRules: [
										{
											condition: 'equals',
											value: true,
											color: {
												type: 'theme',
												themeColor: 'success',
											},
										},
										{
											condition: 'equals',
											value: false,
											color: {
												type: 'theme',
												themeColor: 'textSecondary',
											},
										},
									],
								},
								valueMapping: [
									{
										value: true,
										displayText: 'Yes',
									},
									{
										value: false,
										displayText: 'No',
									},
								],
							},
							displayStyle: 'text',
							conditionalVisibility: {
								field: 'isInherited',
								condition: 'equals',
								value: false,
							},
						},
					],
					sortable: false,
					width: 170,
					verticalAlignment: 'top',
				},
				{
					id: 'order-approval-limit',
					headers: [
						{
							id: 'order-approval-limit-header',
							text: 'Order Approval Limit',
						},
					],
					fields: [
						{
							id: 'order-approval-limit-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'orderApprovalLimit',
									objectTable: 'ecommerce_user_client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					width: 180,
					verticalAlignment: 'top',
				},
				{
					id: 'enabled',
					headers: [
						{
							id: 'enabled-header',
							text: 'Enabled',
						},
					],
					fields: [
						{
							id: 'enabled-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isEnabled',
									objectTable: 'ecommerce_user_client',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'conditional',
									conditionalRules: [
										{
											condition: 'equals',
											value: true,
											color: {
												type: 'theme',
												themeColor: 'success',
											},
										},
										{
											condition: 'equals',
											value: false,
											color: {
												type: 'theme',
												themeColor: 'textSecondary',
											},
										},
									],
								},
								valueMapping: [
									{
										value: true,
										displayText: 'Yes',
									},
									{
										value: false,
										displayText: 'No',
									},
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 100,
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					headers: [
						{
							id: 'actions-header',
							text: '',
						},
					],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							// PD-16789 — inherited rows are read-only; their access is
							// changed on the link that grants it.
							conditionalVisibility: {
								field: 'isInherited',
								condition: 'equals',
								value: false,
							},
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editClient',
									// PD-16789 — also per action, for the phone card
									// layout, which does not apply the field rule above.
									conditionalVisibility: {
										field: 'isInherited',
										condition: 'equals',
										value: false,
									},
								},
								{
									id: 'enable-action',
									label: 'Enable',
									iconName: 'check-circle',
									callbackName: 'toggleEnabled',
									conditionalVisibility: {
										field: 'isEnabled',
										condition: 'equals',
										value: false,
									},
								},
								{
									id: 'disable-action',
									label: 'Disable',
									iconName: 'ban',
									callbackName: 'toggleEnabled',
									conditionalVisibility: {
										field: 'isEnabled',
										condition: 'equals',
										value: true,
									},
								},
								{
									id: 'remove-action',
									label: 'Remove',
									iconName: 'trash',
									callbackName: 'removeClient',
									conditionalVisibility: {
										field: 'isInherited',
										condition: 'equals',
										value: false,
									},
								},
							],
						},
					],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
					width: 140,
				},
			],
			defaultSortId: 'clientName-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'clientName-asc',
			name: 'Client Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'clientName',
					objectTable: 'ecommerce_user_client',
				},
			],
		},
		{
			id: 'clientName-desc',
			name: 'Client Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'clientName',
					objectTable: 'ecommerce_user_client',
				},
			],
		},
	],
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'ecommerce_user_client',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'clientName', fieldLabel: 'Client Name' },
				{ fieldName: 'orderApprovalLimit', fieldLabel: 'Order Approval Limit' },
				{ fieldName: 'isEnabled', fieldLabel: 'Enabled' },
				{ fieldName: 'accessSource', fieldLabel: 'Access' },
				{ fieldName: 'inheritedVia', fieldLabel: 'Inherited Via' },
				{ fieldName: 'includeChildClients', fieldLabel: 'Child Accounts' },
				{
					fieldName: 'includeOwnershipClients',
					fieldLabel: 'Ownership Accounts',
				},
			],
		},
	],
};
